/**
 * What a pending note action is about (#16110) — the client-safe half.
 *
 * The confirmation dialog lists the people and priorities a chat-saved note
 * will be about and lets the person remove any before confirming. Removing
 * one edits the pending action's `input` as well as its `subjects`, because
 * the confirm posts `input` and the server links exactly that.
 *
 * Client-safe: no Prisma, no `server-only`.
 */

import type { WorkspacePendingAction, WorkspacePendingSubject } from './types'

/** The note capability input fields that name subjects, by kind. */
export const NOTE_SUBJECT_INPUT_KEYS = {
  person: 'aboutPersonIds',
  priority: 'aboutPriorityIds',
} as const satisfies Record<WorkspacePendingSubject['kind'], string>

/** `pending` without one subject, in what it shows and what it posts. */
export function withoutPendingSubject(
  pending: WorkspacePendingAction,
  subject: Pick<WorkspacePendingSubject, 'kind' | 'id'>,
): WorkspacePendingAction {
  const key = NOTE_SUBJECT_INPUT_KEYS[subject.kind]
  const current = pending.input[key]
  const remaining = Array.isArray(current)
    ? current.filter(id => id !== subject.id)
    : []
  const { [key]: _removed, ...rest } = pending.input
  return {
    ...pending,
    input: remaining.length > 0 ? { ...rest, [key]: remaining } : rest,
    subjects: (pending.subjects ?? []).filter(
      entry => !(entry.kind === subject.kind && entry.id === subject.id),
    ),
  }
}

/** "A", "A and B", "A, B and C". */
function joinNames(names: readonly string[]): string {
  if (names.length <= 1) return names.join('')
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
}

/**
 * The dialog's privacy sentence — "Mark Smith can’t see this note." — for
 * the people who will be linked but cannot read it, or null when there are
 * none. Linking never shares.
 */
export function noteSubjectsPrivacyLine(
  subjects: readonly WorkspacePendingSubject[],
): string | null {
  const unseen = subjects
    .filter(subject => subject.kind === 'person' && subject.canRead !== true)
    .map(subject => subject.label)
  if (unseen.length === 0) return null
  return `${joinNames(unseen)} can’t see this note.`
}
